// src/pages/TeamPage.js
import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:7071' : '';

function TeamPage() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for the invite form
    const [newUserEmail, setNewUserEmail] = useState('');
    const [isNewUserAdmin, setIsNewUserAdmin] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // Used for both Invite and Delete actions

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setError(null); // Clear previous errors on refresh
        try {
            const response = await fetch(`${API_BASE_URL}/api/users`);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || "Failed to fetch user list.");
            }
            const data = await response.json();
            setUsers(data || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch the list of users when the page first loads
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleInvite = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: newUserEmail,
                    isAdmin: isNewUserAdmin
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || "Failed to invite user.");
            }
            
            alert(`Successfully invited ${newUserEmail}.`);
            // Reset form and refresh the user list
            setNewUserEmail('');
            setIsNewUserAdmin(false);
            fetchUsers();

        } catch (e) {
            setError(`Invite failed: ${e.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- NEW: FUNCTION TO HANDLE DELETING A USER ---
    const handleDelete = async (userId, userEmail) => {
        if (!window.confirm(`Are you sure you want to remove ${userEmail} from your organization?`)) {
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || "Failed to delete user.");
            }
            
            alert(`Successfully removed ${userEmail}.`);
            // Refresh the user list to show the change
            fetchUsers();

        } catch (e) {
            setError(`Delete failed: ${e.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    // ------------------------------------------------

    if (isLoading) return <p>Loading team members...</p>;

    return (
        <div className="team-management-page">
            <h2>Team Management</h2>
            <p>Here is a list of all users in your organization with access to Compliance Guardian.</p>
            
            <table>
                <thead>
                    <tr>
                        <th>User Email</th>
                        <th>Role</th>
                        <th>Actions</th> {/* <-- ADDED: New column for actions */}
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.Id}>
                            <td>{user.UserPrincipalName}</td>
                            <td>{user.IsAdmin ? 'Admin' : 'Member'}</td>
                            {/* --- ADDED: Delete button cell --- */}
                            <td>
                                <button 
                                    className="button-delete" 
                                    onClick={() => handleDelete(user.Id, user.UserPrincipalName)}
                                    disabled={isSubmitting}
                                >
                                    Remove
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            
            <hr />

            <h3>Invite New User</h3>
            <form onSubmit={handleInvite} className="settings-form">
                <div className="form-field">
                    <label htmlFor="email">New User's Email:</label>
                    <input
                        id="email"
                        type="email"
                        placeholder="e.g., colleague@your-company.com"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        required
                    />
                </div>
                <div className="form-field-checkbox">
                    <input
                        id="isAdmin"
                        type="checkbox"
                        checked={isNewUserAdmin}
                        onChange={(e) => setIsNewUserAdmin(e.target.checked)}
                    />
                    <label htmlFor="isAdmin">Make this user an administrator?</label>
                </div>
                {error && <p className="error">{error}</p>}
                <button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Invite User'}
                </button>
            </form>
        </div>
    );
}

export default TeamPage;